import { IRootState } from "../../../../store/index";
import { theme } from "../../../../theme";
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";

import { Button, Paragraph } from "../../../../components/atoms";
import { Input } from "../../../../components/molecules";
import {
  BookingLocationPresenter,
  inflateBookingLocationPresenter,
} from "../../../../presenters/booking-location";
import {
  ApiAddress,
  fetchUserAddresses,
  saveUserAddress,
} from "../../../../services/api/addresses";
import { actionCreators as postcodeActions } from "../../../../store/postcodes/actions";
import { hasAddress } from "../../../../utils/addresses";
import Helmet from "react-helmet";
import { RouteComponentProps, withRouter } from "react-router";

interface IReduxProps {
  bookingLocation?: BookingLocationPresenter;
}

interface IState {
  line_1: string;
  line_2: string;
  postcode: string;
  outcode: string;
  errorMessage?: string;
  addresses: ApiAddress[];
}

type Props = IReduxProps & RouteComponentProps & { dispatch: Dispatch<any> };

const ErrorParagraph = Paragraph.extend`
  color: ${theme.color.error};

  a {
    color: ${theme.color.error};
  }
`;

const ContinueButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
`;

const FormRow = styled.div`
  margin: 16px 0;
  display: flex;
  flex-direction: row;

  > div {
    flex: 1;
  }
`;

class BookingAddress extends React.Component<Props, IState> {
  private timer?: number;
  public state: IState = {
    errorMessage: undefined,
    line_1: "",
    line_2: "",
    postcode: "",
    outcode: "",
    addresses: [],
  };

  public async componentDidMount() {
    if (this.props.bookingLocation) {
      const checkoutAddress = this.props.bookingLocation.getCheckoutAddress();
      const line_1 =
        checkoutAddress.address1 !== "unknown" ? checkoutAddress.address1 : "";
      const line_2 =
        checkoutAddress.address2 !== "unknown" ? checkoutAddress.address2 : "";
      const addresses = await fetchUserAddresses();

      this.setState({
        line_1,
        line_2,
        postcode: checkoutAddress.postcode,
        outcode: checkoutAddress.postcode.replace(/\s.+?$/, ""),
        addresses,
      });
    }
  }

  public componentWillUnmount() {
    window.clearTimeout(this.timer);
  }

  private readonly handleSubmit = async (): Promise<void> => {
    if (this.state.line_1.trim().length < 2) {
      // Expect at least two characters
      this.setState({
        ...this.state,
        errorMessage: "Please complete the first line of your address",
      });
    } else {
      const address = {
        line_1: this.state.line_1,
        line_2: this.state.line_2,
        postcode: this.state.postcode,
      };

      const checkoutAddress = {
        address1: address.line_1,
        address2: address.line_2,
        postcode: address.postcode,
      };

      if (!hasAddress(checkoutAddress, this.state.addresses)) {
        try {
          await saveUserAddress(checkoutAddress);
        } catch (err: any) {
          this.setState({ errorMessage: err.message });
          return;
        }
      }

      this.props.dispatch(postcodeActions.setAddressManually(address));
      this.props.dispatch(postcodeActions.setBillingAddressManually(address));

      // prevents double mounting on checkout page during transition
      this.timer = window.setTimeout(() => {
        this.props.history.replace("/booking");
      });
    }
  };

  /**
   * Render the address form. This allows the user to enter the rest of their address
   * after they have found a treatment using their postcode area only. It performs only
   * basic validation.
   */
  public render(): JSX.Element {
    const error: JSX.Element | undefined =
      typeof this.state.errorMessage !== "undefined" ? (
        <ErrorParagraph>{this.state.errorMessage}</ErrorParagraph>
      ) : undefined;

    return (
      <React.Fragment>
        <Helmet>
          <title>Ruuby - Checkout - Address</title>
        </Helmet>

        <Paragraph>Please provide the full address to continue</Paragraph>

        <FormRow>
          <Input
            label="First line of address"
            name="line_1"
            value={this.state.line_1}
            onChange={(e) =>
              this.setState({ ...this.state, line_1: e.currentTarget.value })
            }
          />
        </FormRow>

        <FormRow>
          <Input
            label="Second line of address"
            name="line_2"
            value={this.state.line_2}
            onChange={(e) =>
              this.setState({ ...this.state, line_2: e.currentTarget.value })
            }
          />
        </FormRow>

        <FormRow>
          <Input
            label={`Postcode`}
            name="postcode"
            value={this.state.postcode}
            onChange={(e) =>
              this.setState({
                ...this.state,
                postcode: e.currentTarget.value.toUpperCase(),
              })
            }
          />
        </FormRow>

        {error}

        <ContinueButton size="small" onClick={this.handleSubmit}>
          Save Address And Continue
        </ContinueButton>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: IRootState): IReduxProps => {
  return {
    ...(state.postcodesState.bookingLocation && {
      bookingLocation: inflateBookingLocationPresenter(
        state.postcodesState.bookingLocation,
      ),
    }),
  };
};

export const BookingAddressForm = withRouter(
  connect<IReduxProps, {}, {}, IRootState>(mapStateToProps)(BookingAddress),
);
